<template>
  <button
    v-bind="$attrs"
    class="submit-btn btn"
    :class="[
      {
        'submit-btn--loading': loading,
        'btn-link': variant === 'link'
      },
      `submit-btn--${size}`,
      `btn-${size}`
    ]"
    :disabled="loading || disabled"
  >
    <span class="submit-btn__inner">
      <span
        class="submit-btn__title"
        data-testid="submit-button"
      >
        <slot />
      </span>
      <span
        class="submit-btn__spinner"
        :class="`submit-btn__spinner--${spinnerSize}`"
      />
    </span>
  </button>
</template>

<script>
const validSizes = [
  'xs',
  'sm',
  'md',
  'lg'
]
const validSpinnerSizes = [
  'xxs',
  ...validSizes
]
const validVariants = ['link']

export default {
  props: {
    loading: Boolean,
    disabled: Boolean,
    size: {
      default: 'md',
      type: String,
      validator(value) {
        return !value || validSizes.includes(value)
      }
    },
    variant: {
      type: String,
      default: '',
      validator(value) {
        return !value || validVariants.includes(value)
      }
    }
  },
  computed: {
    spinnerSize() {
      if (this.variant === 'link') {
        // spinner size needs to be one smaller than the button size
        const indexOfSize = validSpinnerSizes.indexOf(this.size)
        const indexOfOneSizeSmaller = indexOfSize - 1

        if (indexOfOneSizeSmaller >= 0 && validSpinnerSizes[indexOfOneSizeSmaller]) {
          return validSpinnerSizes[indexOfOneSizeSmaller]
        }
      }
      return this.size
    }
  }
}
</script>

<style lang="scss">
@import "~@ui/styles/src/scss/utils/lib";

@mixin spinnerSize($size) {
  width: $size;
  height: $size;
  margin-top: -($size * 0.5);
  margin-left: -($size * 0.5);
}

@keyframes spin {
  0% { transform: rotate3d(0, 0, 1, 0); }
  25% { transform: rotate3d(0, 0, 1, 90deg); }
  50% { transform: rotate3d(0, 0, 1, 180deg); }
  100% { transform: rotate3d(0, 0, 1, 360deg); }
}

.submit-btn {
  $this: &;

  position: relative;
  overflow: hidden;

  &__spinner {
    position: absolute;
    top: 50%;
    left: 100%;
    display: block;
    border: 2px solid $white20percent;
    border-left-color: $white90percent;
    border-radius: 100%;
    opacity: 0;

    #{$this}--loading & {
      left: 50%;
      opacity: 1;
      transition: 0.1s linear left;
      animation: spin infinite 0.75s linear;
    }

    &--xxs {
      @include spinnerSize(10px);
    }

    &--xs {
      @include spinnerSize(12px);
    }

    &--sm {
      @include spinnerSize(18px);
    }

    &--md {
      @include spinnerSize(24px);
    }

    &--lg {
      @include spinnerSize(30px);
    }

    /* Change the color of spinner for outlined and light btns */
    #{$this}.btn-light &,
    #{$this}.btn-outline-primary &,
    #{$this}.btn-outline-secondary &,
    #{$this}.btn-outline-success &,
    #{$this}.btn-outline-danger &,
    #{$this}.btn-outline-light &,
    #{$this}.btn-outline-dark & {
      border-color: $black20percent;
      border-left-color: $primaryColor;
    }
  }

  &__inner {
    @extend %flex-centered;
  }

  &__title {
    @extend %inline-flex-row-space-between;

    align-items: center;

    #{$this}--loading & {
      left: -100%;
      opacity: 0;
      transition: 0.1s linear left;
    }
  }
}
</style>
